import { authApi } from "../lib/api"; // Changed to relative path
import { User } from "../lib/types/userTypes"; // Changed to relative path
import { RegisterRequest } from "../lib/types/authTypes"; // Changed to relative path
import { setAuthTokens, setAuthRefreshHandler } from "../lib/api/config"; // Import setAuthTokens and setAuthRefreshHandler
import axios from "axios"; // Added axios import
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios"; // For typing axios errors

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  googleLogin: (code: string) => Promise<void>;
  register: (
    data: RegisterRequest
  ) => Promise<{ requiresVerification?: boolean; message?: string }>;
  logout: () => Promise<void>;
  verifyEmail: (email: string, code: string) => Promise<void>;
  resendVerificationCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (
    email: string,
    code: string,
    newPassword: string
  ) => Promise<void>;
  handleRefreshToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Helper to extract error message
const getErrorMessage = (error: any, defaultMessage: string): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>;
    return axiosError.response?.data?.message || defaultMessage;
  }
  return defaultMessage;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  const clearAuthData = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setAuthTokens(null, null); // Clear tokens in apiClient
    console.log(
      "[AuthContext] Auth data cleared and tokens reset in apiClient."
    );
  }, []);

  const loadAuthState = useCallback(() => {
    const storedUser = localStorage.getItem("user");
    const storedAccessToken = localStorage.getItem("accessToken");
    const storedRefreshToken = localStorage.getItem("refreshToken");

    if (storedUser && storedAccessToken && storedRefreshToken) {
      try {
        setUser(JSON.parse(storedUser) as User);
        setAccessToken(storedAccessToken);
        setRefreshToken(storedRefreshToken);
        setAuthTokens(storedAccessToken, storedRefreshToken); // Set tokens in apiClient
        console.log(
          "[AuthContext] Auth data loaded from localStorage and tokens set in apiClient."
        );
      } catch (error) {
        console.error("Error parsing auth data from localStorage:", error);
        clearAuthData();
      }
    }
    setIsLoading(false);
  }, [clearAuthData]);

  useEffect(() => {
    loadAuthState();
  }, [loadAuthState]);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await authApi.login({ email, password }); // Returns BackendResponse<LoginResponseData>

      if (response.status === "success" && response.data) {
        const { user: loggedInUser, tokens } = response.data;
        setUser(loggedInUser);
        setAccessToken(tokens.accessToken);
        setRefreshToken(tokens.refreshToken);

        localStorage.setItem("user", JSON.stringify(loggedInUser));
        localStorage.setItem("accessToken", tokens.accessToken);
        localStorage.setItem("refreshToken", tokens.refreshToken);
        setAuthTokens(tokens.accessToken, tokens.refreshToken); // Set tokens in apiClient
        console.log("[AuthContext] Login successful, tokens set in apiClient.");

        toast.success(response.message || "Login successful!");
        // navigate("/dashboard"); // Or appropriate route
      } else {
        throw new Error(
          response.message ||
            "Login failed due to unexpected response structure."
        );
      }
    } catch (error: any) {
      console.error("Login error:", error);
      const errorMessage = getErrorMessage(
        error,
        "Login failed. Please try again."
      );
      toast.error(errorMessage);
      if (errorMessage.includes("Email not verified")) {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
      }
      throw error; // Re-throw to allow components to handle it if needed
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterRequest) => {
    setIsLoading(true);
    try {
      const response = await authApi.register(data); // Returns BackendResponse<null, User>
      toast.success(response.message || "Registration request sent.");

      if (response.requiresVerification) {
        navigate(`/verify-email?email=${encodeURIComponent(data.email)}`);
      }
      return {
        requiresVerification: response.requiresVerification,
        message: response.message,
      };
    } catch (error: any) {
      console.error("Register error:", error);
      toast.error(getErrorMessage(error, "Registration failed."));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      if (accessToken) {
        await authApi.logout();
      }
    } catch (error: any) {
      console.error("Logout API error:", error);
      // toast.error("Logout failed on server, but logging out locally.");
    } finally {
      clearAuthData();
      toast.success("Logged out successfully");
      navigate("/login");
      setIsLoading(false);
    }
  };

  const verifyEmail = async (email: string, code: string) => {
    setIsLoading(true);
    try {
      const response = await authApi.verifyEmail({ email, code });
      toast.success(response.message || "Email verified successfully!");
      navigate("/login");
    } catch (error: any) {
      console.error("Email verification error:", error);
      toast.error(getErrorMessage(error, "Email verification failed."));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resendVerificationCode = async (email: string) => {
    setIsLoading(true);
    try {
      const response = await authApi.resendVerificationCode({ email });
      toast.success(response.message || "Verification code resent.");
    } catch (error: any) {
      console.error("Resend verification code error:", error);
      toast.error(getErrorMessage(error, "Failed to resend code."));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email: string) => {
    setIsLoading(true);
    try {
      const response = await authApi.forgotPassword({ email });
      toast.success(response.message || "Password reset instructions sent.");
    } catch (error: any) {
      console.error("Forgot password error:", error);
      toast.error(getErrorMessage(error, "Failed to send reset instructions."));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = async (code: string) => {
    setIsLoading(true);
    try {
      const response = await authApi.googleLogin({ code });
      if (response.status === "success" && response.data) {
        const { user: loggedInUser, tokens } = response.data;
        setUser(loggedInUser);
        setAccessToken(tokens.accessToken);
        setRefreshToken(tokens.refreshToken);

        localStorage.setItem("user", JSON.stringify(loggedInUser));
        localStorage.setItem("accessToken", tokens.accessToken);
        localStorage.setItem("refreshToken", tokens.refreshToken);
        setAuthTokens(tokens.accessToken, tokens.refreshToken);

        toast.success(response.message || "Login successful!");
        navigate("/dashboard");
      } else {
        throw new Error(
          response.message ||
            "Google login failed due to unexpected response structure."
        );
      }
    } catch (error: any) {
      console.error("Google login error:", error);
      const errorMessage = getErrorMessage(
        error,
        "Google login failed. Please try again."
      );
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (
    email: string,
    code: string,
    newPassword: string
  ) => {
    setIsLoading(true);
    try {
      const response = await authApi.resetPassword({
        email,
        code,
        newPassword,
      });
      toast.success(response.message || "Password reset successfully!");
      navigate("/login");
    } catch (error: any) {
      console.error("Reset password error:", error);
      toast.error(getErrorMessage(error, "Failed to reset password."));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshToken = useCallback(async (): Promise<string | null> => {
    const currentRefreshToken = localStorage.getItem("refreshToken");
    if (!currentRefreshToken) {
      console.log(
        "[AuthContext] handleRefreshToken: No refresh token in localStorage. Clearing auth data."
      );
      clearAuthData();
      return null;
    }

    console.log(
      `[AuthContext] handleRefreshToken: Attempting refresh with token: ${currentRefreshToken.substring(
        0,
        10
      )}...`
    );

    try {
      // authApi.refreshToken returns BackendResponse<RefreshTokenData>
      const response = await authApi.refreshToken({
        refreshToken: currentRefreshToken,
      });

      if (response.status === "success" && response.data) {
        const {
          accessToken: newAccessToken,
          refreshToken: newRotatedRefreshToken,
        } = response.data;

        console.log(
          `[AuthContext] handleRefreshToken: Refresh successful. New access token: ${newAccessToken.substring(
            0,
            10
          )}...`
        );
        console.log(
          `[AuthContext] handleRefreshToken: New refresh token (if rotated): ${
            newRotatedRefreshToken
              ? newRotatedRefreshToken.substring(0, 10) + "..."
              : "none"
          }`
        );
        console.log(
          `[AuthContext] handleRefreshToken: Raw refresh response data:`,
          response.data
        );

        setAccessToken(newAccessToken);
        localStorage.setItem("accessToken", newAccessToken);
        if (newRotatedRefreshToken) {
          setRefreshToken(newRotatedRefreshToken);
          localStorage.setItem("refreshToken", newRotatedRefreshToken);
        }
        setAuthTokens(
          newAccessToken,
          newRotatedRefreshToken || currentRefreshToken
        ); // Set tokens in apiClient
        console.log(
          "[AuthContext] Refresh successful, tokens set in apiClient."
        );

        console.log(
          `[AuthContext] handleRefreshToken: Tokens updated in localStorage. Access: ${localStorage
            .getItem("accessToken")
            ?.substring(0, 10)}..., Refresh: ${localStorage
            .getItem("refreshToken")
            ?.substring(0, 10)}...`
        );

        return newAccessToken;
      } else {
        console.warn(
          "[AuthContext] handleRefreshToken: Refresh token API returned success status but no data or unexpected response."
        );
        throw new Error(
          response.message || "Refresh token failed due to unexpected response."
        );
      }
    } catch (error) {
      console.error(
        "[AuthContext] handleRefreshToken: Token refresh error:",
        error
      );
      clearAuthData(); // This will also call setAuthTokens(null, null)
      if (window.location.pathname !== "/login") navigate("/login"); // Avoid navigation loop if already on login
      return null;
    }
  }, [navigate, clearAuthData]);

  // Register the handleRefreshToken with the apiClient's interceptor
  useEffect(() => {
    setAuthRefreshHandler(handleRefreshToken);
  }, [handleRefreshToken]); // Dependency on handleRefreshToken to ensure it's stable

  const isAuthenticated = !!accessToken;

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isLoading,
        isAuthenticated,
        login,
        googleLogin,
        register,
        logout,
        verifyEmail,
        resendVerificationCode,
        forgotPassword,
        resetPassword,
        handleRefreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
